import { memo, type ReactNode } from "react";
import katex from "katex";

const inlineTokens = /<(\/?)(sup|sub|i|em|b|strong)>|(?<!\\)\$\$([\s\S]{1,10000}?)(?<!\\)\$\$|(?<!\\)\$((?:\\\$|[^$\n]){1,5000}?)(?<!\\)\$/gu;
type InlineTag = "sup" | "sub" | "i" | "em" | "b" | "strong";

function plainText(text: string): string {
  return text.replace(/\\([$*])/gu, "$1");
}

function renderInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const stack: { tag: InlineTag; key: number; children: ReactNode[] }[] = [];
  function append(part: ReactNode): void {
    (stack.at(-1)?.children ?? parts).push(part);
  }
  let offset = 0;
  for (const match of text.matchAll(inlineTokens)) {
    const index = match.index;
    append(plainText(text.slice(offset, index)));
    const tag = match[2];
    if (tag) {
      // Only these attribute-free formatting tags become elements. All other
      // source HTML remains escaped text; it never enters the DOM as markup.
      if (match[1] === "/" && stack.at(-1)?.tag === tag) {
        const frame = stack.pop()!;
        const Tag = frame.tag;
        append(<Tag key={frame.key}>{frame.children}</Tag>);
      } else if (!match[1] && stack.length < 32) {
        stack.push({ tag: tag as InlineTag, key: index, children: [] });
      } else {
        append(match[0]);
      }
    } else {
      const formula = match[3] ?? match[4] ?? "";
      const display = match[3] !== undefined;
      // Unescaped currency such as "$100 to $200" is prose, not a formula.
      const prosePrice = /^\s*\d/u.test(formula) && /[a-zA-Z]{2,}/u.test(formula) && !/\\[a-zA-Z]/u.test(formula);
      const currencyPair = /^\s*\d/u.test(formula) && /^\d/u.test(text.slice(index + match[0].length));
      const mathematical = !prosePrice && !currencyPair && (display || /\\[a-zA-Z]|[=^_{}+*/-]|^\s*(?:[\d.,]+|[a-zA-Z])\s*$/u.test(formula));
      if (!mathematical) {
        append(plainText(match[0]));
      } else {
        try {
          // Native MathML respects the site's strict style CSP. KaTeX's
          // simulated bold (pmb) uses inline CSS, so use mathematical bold.
          const html = katex.renderToString(formula.replace(/\\pmb\b/gu, "\\boldsymbol"), {
            displayMode: display,
            output: "mathml",
            throwOnError: true,
            trust: false,
            strict: "ignore",
            maxSize: 20,
            maxExpand: 500,
          });
          append(<span key={index} className={display ? "reading-math display" : "reading-math"} dangerouslySetInnerHTML={{ __html: html }} />);
        } catch {
          // Damaged OCR must remain readable without breaking the whole issue.
          append(<span key={index} className="reading-math-source">{plainText(match[0])}</span>);
        }
      }
    }
    offset = index + match[0].length;
  }
  append(plainText(text.slice(offset)));
  while (stack.length) {
    const frame = stack.pop()!;
    append(`<${frame.tag}>`);
    for (const child of frame.children) append(child);
  }
  return parts;
}

export const ReadingInline = memo(function ReadingInline({ text }: { text: string }): React.JSX.Element {
  return <>{renderInline(text)}</>;
});
